// Servicio de Proveedores
// Gestiona los proveedores del sistema

import { getCollection, getDocument, updateDocument } from './firestore.service'
import { Timestamp, doc, setDoc } from 'firebase/firestore'
import { db } from './firebase.config'

const SUPPLIERS_COLLECTION = 'suppliers'
const PURCHASE_ORDERS_COLLECTION = 'purchaseOrders'

/**
 * Obtener todos los proveedores
 * @param {boolean} includeInactive - Si incluir proveedores inactivos
 * @returns {Promise<Array>} Lista de proveedores
 */
export const getAllSuppliers = async (includeInactive = false) => {
  try {
    // Obtener todos los proveedores sin filtros para evitar índices compuestos
    const allSuppliers = await getCollection(SUPPLIERS_COLLECTION, [])
    
    // Filtrar por activos si es necesario
    let filtered = allSuppliers
    if (!includeInactive) {
      filtered = allSuppliers.filter(supplier => supplier.active !== false)
    }
    
    // Ordenar por nombre
    return filtered.sort((a, b) => {
      const nameA = (a.name || '').toLowerCase()
      const nameB = (b.name || '').toLowerCase()
      return nameA.localeCompare(nameB)
    })
  } catch (error) {
    console.error('Error obteniendo proveedores:', error)
    throw error
  }
}

/**
 * Obtener un proveedor por ID (email)
 * @param {string} supplierId - ID del proveedor (email)
 * @returns {Promise<Object|null>} Proveedor o null
 */
export const getSupplier = async (supplierId) => {
  try {
    return await getDocument(SUPPLIERS_COLLECTION, supplierId)
  } catch (error) {
    console.error('Error obteniendo proveedor:', error)
    throw error
  }
}

/**
 * Crear un nuevo proveedor
 * @param {Object} supplierData - Datos del proveedor
 * @returns {Promise<string>} ID del proveedor creado (email)
 */
export const createSupplier = async (supplierData) => {
  try {
    const data = {
      name: supplierData.name.trim(),
      businessName: supplierData.businessName?.trim() || '',
      taxId: supplierData.taxId?.trim() || '',
      email: supplierData.email.trim().toLowerCase(),
      phone: supplierData.phone?.trim() || '',
      phoneAlternate: supplierData.phoneAlternate?.trim() || '',
      addresses: supplierData.addresses || [],
      primaryAddressIndex: supplierData.primaryAddressIndex !== undefined ? supplierData.primaryAddressIndex : 0,
      paymentTerms: supplierData.paymentTerms?.trim() || '',
      deliveryTime: supplierData.deliveryTime?.trim() || '',
      minimumOrder: supplierData.minimumOrder ? parseFloat(supplierData.minimumOrder) : null,
      active: supplierData.active !== false,
      rating: supplierData.rating ? parseFloat(supplierData.rating) : null,
      productIds: supplierData.productIds || [],
      notes: supplierData.notes?.trim() || '',
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      createdBy: supplierData.createdBy || null,
      lastUpdatedBy: supplierData.lastUpdatedBy || null
    }
    
    // Usar email como ID del documento
    await setDoc(doc(db, SUPPLIERS_COLLECTION, data.email), data)
    
    return data.email
  } catch (error) {
    console.error('Error creando proveedor:', error)
    throw error
  }
}

/**
 * Actualizar un proveedor
 * @param {string} supplierId - ID del proveedor (email)
 * @param {Object} supplierData - Datos actualizados
 * @returns {Promise<void>}
 */
export const updateSupplier = async (supplierId, supplierData) => {
  try {
    // Preparar datos sin incluir campos que no deben actualizarse
    const data = {
      name: supplierData.name?.trim(),
      businessName: supplierData.businessName?.trim() || '',
      taxId: supplierData.taxId?.trim() || '',
      email: supplierData.email?.trim().toLowerCase() || supplierId,
      phone: supplierData.phone?.trim() || '',
      phoneAlternate: supplierData.phoneAlternate?.trim() || '',
      addresses: supplierData.addresses || [],
      primaryAddressIndex: supplierData.primaryAddressIndex !== undefined ? supplierData.primaryAddressIndex : 0,
      paymentTerms: supplierData.paymentTerms?.trim() || '',
      deliveryTime: supplierData.deliveryTime?.trim() || '',
      minimumOrder: supplierData.minimumOrder ? parseFloat(supplierData.minimumOrder) : null,
      rating: supplierData.rating ? parseFloat(supplierData.rating) : null,
      productIds: supplierData.productIds || [],
      notes: supplierData.notes?.trim() || '',
      active: supplierData.active !== undefined ? supplierData.active : true
    }
    
    // Solo incluir lastUpdatedBy si está definido
    if (supplierData.lastUpdatedBy !== undefined) {
      data.lastUpdatedBy = supplierData.lastUpdatedBy || null
    }
    
    // No incluir createdBy en actualizaciones (solo se usa al crear)
    
    return await updateDocument(SUPPLIERS_COLLECTION, supplierId, data)
  } catch (error) {
    console.error('Error actualizando proveedor:', error)
    throw error
  }
}

/**
 * Eliminar (desactivar) un proveedor
 * @param {string} supplierId - ID del proveedor (email)
 * @returns {Promise<void>}
 */
export const deleteSupplier = async (supplierId) => {
  try {
    // En lugar de eliminar, desactivamos
    return await updateDocument(SUPPLIERS_COLLECTION, supplierId, {
      active: false,
      updatedAt: Timestamp.now()
    })
  } catch (error) {
    console.error('Error eliminando proveedor:', error)
    throw error
  }
}

/**
 * Obtener productos de un proveedor
 * @param {string} supplierId - ID del proveedor (email)
 * @returns {Promise<Array>} Lista de productos
 */
export const getSupplierProducts = async (supplierId) => {
  try {
    // Obtener todos los productos y filtrar por supplierId
    const allProducts = await getCollection('products', [])
    
    return allProducts.filter(product => {
      // Compatibilidad: buscar en supplierIds (nuevo) o supplierId (antiguo)
      if (product.supplierIds && Array.isArray(product.supplierIds)) {
        return product.supplierIds.includes(supplierId)
      }
      // Compatibilidad con datos antiguos
      if (product.supplierId === supplierId) return true
      return false
    })
  } catch (error) {
    console.error('Error obteniendo productos del proveedor:', error)
    throw error
  }
}

/**
 * Obtener pedidos de compra de un proveedor
 * @param {string} supplierId - ID del proveedor (email)
 * @returns {Promise<Array>} Lista de pedidos de compra
 */
export const getSupplierPurchaseOrders = async (supplierId) => {
  try {
    // Obtener todos los pedidos de compra y filtrar por supplierId
    const allOrders = await getCollection(PURCHASE_ORDERS_COLLECTION, [])
    
    return allOrders
      .filter(order => order.supplierId === supplierId)
      .sort((a, b) => {
        const dateA = a.createdAt?.seconds || 0
        const dateB = b.createdAt?.seconds || 0
        return dateB - dateA // Más recientes primero
      })
  } catch (error) {
    console.error('Error obteniendo pedidos de compra del proveedor:', error)
    throw error
  }
}

/**
 * Obtener estadísticas de un proveedor
 * @param {string} supplierId - ID del proveedor (email)
 * @returns {Promise<Object>} Estadísticas del proveedor
 */
export const getSupplierStats = async (supplierId) => {
  try {
    const [products, purchaseOrders] = await Promise.all([
      getSupplierProducts(supplierId),
      getSupplierPurchaseOrders(supplierId)
    ])
    
    const totalOrders = purchaseOrders.length
    const totalSpent = purchaseOrders.reduce((sum, order) => sum + (order.total || 0), 0)
    const averageOrder = totalOrders > 0 ? totalSpent / totalOrders : 0
    
    // Obtener fechas
    let firstOrderDate = null
    let lastOrderDate = null
    
    if (purchaseOrders.length > 0) {
      const dates = purchaseOrders
        .map(order => {
          if (order.createdAt?.seconds) {
            return new Date(order.createdAt.seconds * 1000)
          }
          return null
        })
        .filter(Boolean)
        .sort((a, b) => a - b)
      
      if (dates.length > 0) {
        firstOrderDate = dates[0]
        lastOrderDate = dates[dates.length - 1]
      }
    }
    
    // Productos más comprados
    const productCounts = {}
    purchaseOrders.forEach(order => {
      if (order.items && Array.isArray(order.items)) {
        order.items.forEach(item => {
          if (item.productId) {
            productCounts[item.productId] = (productCounts[item.productId] || 0) + (item.quantity || 0)
          }
        })
      }
    })
    
    const topProducts = Object.entries(productCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([productId, quantity]) => ({ productId, quantity }))
    
    return {
      totalProducts: products.length,
      totalOrders,
      totalSpent,
      averageOrder,
      firstOrderDate,
      lastOrderDate,
      topProducts
    }
  } catch (error) {
    console.error('Error obteniendo estadísticas del proveedor:', error)
    throw error
  }
}

export default {
  getAllSuppliers,
  getSupplier,
  createSupplier,
  updateSupplier,
  deleteSupplier,
  getSupplierProducts,
  getSupplierPurchaseOrders,
  getSupplierStats
}

